import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { userRequest } from '../utils/requestMethods';

const StateContext = createContext(null);

const initialState = {
  userProfile: false,
  notification: false,
};

const useUser = () => {
  const [user, setUser] = useState(null);
  const [schoolId, setSchoolId] = useState("");
  const [isInitialized, setIsInitialized] = useState(false);
  const attemptedSchoolsRefresh = useRef(false);

  const fetchUserSchools = async () => {
    try {
      const { data } = await userRequest.get('/schools/user');
      const updatedUser = { ...user, schools: { schools: data.schools } };
      setUser(updatedUser);
      
      // Update localStorage with new school data
      const currentTokenWithUser = JSON.parse(localStorage.getItem("tokenWithUser") || "null");
      if (currentTokenWithUser) {
        currentTokenWithUser.schools = { schools: data.schools };
        localStorage.setItem("tokenWithUser", JSON.stringify(currentTokenWithUser));
      }
      
      // Handle school selection when schools are refreshed
      if (Array.isArray(data.schools) && data.schools.length > 0) {
        const savedSchoolId = localStorage.getItem('selectedSchoolId');
        
        // Check if current school ID is still valid
        if (savedSchoolId && data.schools.some(s => s._id === savedSchoolId)) {
          // Current school ID is still valid, keep it
          return data.schools;
        }
        
        // Current school ID is no longer valid, select a new one
        // Prioritize primary/current school over first school
        let selectedSchool = data.schools.find(s => s.is_current === true);
        
        // If no current school, try to find the primary school
        if (!selectedSchool) {
          selectedSchool = data.schools.find(s => s.is_primary === true);
        }
        
        // If still no primary/current school, fallback to first school
        if (!selectedSchool) {
          selectedSchool = data.schools[0];
        }
        
        const selectedSchoolId = selectedSchool._id;
        setSchoolId(selectedSchoolId);
        localStorage.setItem('selectedSchoolId', selectedSchoolId);
      }
      
      return data.schools;
    } catch (error) {
      console.error('Error fetching user schools:', error);
      return null;
    }
  };

  // Function to ensure school selection happens
  const ensureSchoolSelection = useCallback(() => {
    const tokenWithUser = JSON.parse(localStorage.getItem("tokenWithUser") || "null");
    //console.log('ensureSchoolSelection called with tokenWithUser:', tokenWithUser);
    
    if (tokenWithUser?.schools?.schools?.length > 0) {
      const schools = tokenWithUser.schools.schools;
      //console.log('Available schools:', schools);
      
      // First, try to find a school marked as current
      let selectedSchool = schools.find(s => s.is_current === true);
      //console.log('Current school found:', selectedSchool);
      
      // If no current school, try to find the primary school
      if (!selectedSchool) {
        selectedSchool = schools.find(s => s.is_primary === true);
       // console.log('Primary school found:', selectedSchool);
      }
      
      // If still no primary/current school, fallback to first school
      if (!selectedSchool) {
        selectedSchool = schools[0];
        //console.log('Fallback to first school:', selectedSchool);
      }
      
      const selectedSchoolId = selectedSchool._id;
      //console.log('Setting schoolId to:', selectedSchoolId);
      setSchoolId(selectedSchoolId);
      localStorage.setItem('selectedSchoolId', selectedSchoolId);
      return selectedSchoolId;
    }
    console.log('No schools available for selection');
    return null;
  }, []);

  // Effect to ensure schoolId is set whenever user data changes
  useEffect(() => {
    //console.log('User effect triggered - user:', user, 'schoolId:', schoolId);
    if (user && !schoolId && user.schools?.schools?.length > 0) {
      console.log('User has schools but no schoolId, calling ensureSchoolSelection');
      ensureSchoolSelection();
    }
  }, [user, schoolId, ensureSchoolSelection]);

  useEffect(() => {
    const initializeUser = () => {
      console.log('initializeUser called');
      const tokenWithUser = JSON.parse(localStorage.getItem("tokenWithUser") || "null");
      const savedSchoolId = localStorage.getItem('selectedSchoolId');
      //console.log('tokenWithUser:', tokenWithUser);
      console.log('savedSchoolId:', savedSchoolId);

      if (tokenWithUser) {
        setUser(tokenWithUser);

        if (savedSchoolId && tokenWithUser?.schools?.schools?.some(s => s._id === savedSchoolId)) {
          // Use saved school ID if it's still valid
          //console.log('Using saved school ID:', savedSchoolId);
          setSchoolId(savedSchoolId);
          setIsInitialized(true);
        } else if (Array.isArray(tokenWithUser?.schools?.schools) && tokenWithUser.schools.schools.length > 0) {
          // Prioritize primary/current school over first school
          console.log('Selecting new school from available schools');
          const schools = tokenWithUser.schools.schools;
          
          // First, try to find a school marked as current
          let selectedSchool = schools.find(s => s.is_current === true);
          
          // If no current school, try to find the primary school
          if (!selectedSchool) {
            selectedSchool = schools.find(s => s.is_primary === true);
          }
          
          // If still no primary/current school, fallback to first school
          if (!selectedSchool) {
            selectedSchool = schools[0];
          }
          
          const selectedSchoolId = selectedSchool._id;
          //console.log('Selected school ID:', selectedSchoolId);
          setSchoolId(selectedSchoolId);
          localStorage.setItem('selectedSchoolId', selectedSchoolId);
          setIsInitialized(true);
        } else if (tokenWithUser?.school?._id) {
          // Single school fallback
          //console.log('Using single school fallback:', tokenWithUser.school._id);
          setSchoolId(tokenWithUser.school._id);
          //localStorage.setItem('selectedSchoolId', tokenWithUser.school._id);
          setIsInitialized(true);
        } else {
          console.log('No schools available, marking as initialized');
          setIsInitialized(true);
        }
      } else {
        // No user data yet, but mark as initialized to prevent infinite loading
        console.log('No user data available, marking as initialized');
        setIsInitialized(true);
      }
    };

    // Try to initialize immediately
    initializeUser();
    
    // Also try again after a short delay to handle localStorage timing issues
    const timer1 = setTimeout(() => { console.log('Retry 1'); initializeUser(); }, 100);
    const timer2 = setTimeout(() => { console.log('Retry 2'); initializeUser(); }, 500);
    const timer3 = setTimeout(() => { console.log('Retry 3'); initializeUser(); }, 1000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []); // Empty dependency array means this runs only once on mount

  // Safety net: if a cached session has no usable schools data (e.g. stale/incomplete
  // localStorage from before the account was assigned to a school), schoolId can never
  // resolve and every downstream check (like subscription status) silently treats this
  // as "nothing found" instead of "couldn't determine". Re-fetch from the server once
  // so we recover without requiring the user to manually clear their cache.
  useEffect(() => {
    if (
      isInitialized &&
      user &&
      !schoolId &&
      !(user.schools?.schools?.length > 0) &&
      !attemptedSchoolsRefresh.current
    ) {
      attemptedSchoolsRefresh.current = true;
      fetchUserSchools();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialized, user, schoolId]);

  // Function to manually trigger school selection (useful for login flow)
  const selectDefaultSchool = useCallback(() => {
    const result = ensureSchoolSelection();
    if (result) {
      setIsInitialized(true);
    }
    return result;
  }, [ensureSchoolSelection]);

  // Function to force refresh school selection
  const forceRefreshSchoolSelection = useCallback(() => {
    const result = ensureSchoolSelection();
    return result;
  }, [ensureSchoolSelection]);

  return { user, schoolId, setSchoolId, fetchUserSchools, selectDefaultSchool, forceRefreshSchoolSelection, isInitialized };
};

const useSchool = (schoolId) => {
  const [school, setSchool] = useState(null);

  const fetchSchool = async () => {
    if (schoolId) {
      try {
        const { data } = await userRequest.get(`schools/${schoolId}`);
        console.log(data);
        setSchool(data);
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    fetchSchool();
  }, [schoolId]);

  return { school, refreshSchool: fetchSchool };
};

export const ContextProvider = ({ children }) => {
  const [screenSize, setScreenSize] = useState(undefined);
  const currentColor = '#4543B5';
  const [currentMode, setCurrentMode] = useState('Light');
  const [themeSettings, setThemeSettings] = useState(false);
  const [activeMenu, setActiveMenu] = useState(true);
  const [isClicked, setIsClicked] = useState(initialState);

  const { user, schoolId, setSchoolId, fetchUserSchools, selectDefaultSchool, forceRefreshSchoolSelection, isInitialized } = useUser();
  const { school, refreshSchool } = useSchool(schoolId);

  const setMode = useCallback((e) => {
    setCurrentMode(e.target.value);
    localStorage.setItem('themeMode', e.target.value);
  }, []);

  const setColor = useCallback((color) => {
    localStorage.setItem('colorMode', color);
  }, []);

  const handleClick = useCallback((clicked) => setIsClicked({ ...initialState, [clicked]: true }), []);

  const contextValue = useMemo(() => ({
    user,
    school,
    schoolId,
    setSchoolId,
    refreshSchool, // Expose refreshSchool function in context
    fetchUserSchools, // Expose fetchUserSchools function in context
    selectDefaultSchool, // Expose selectDefaultSchool function in context
    forceRefreshSchoolSelection, // Expose forceRefreshSchoolSelection function in context
    isInitialized, // Expose initialization state
    currentColor,
    currentMode,
    activeMenu,
    screenSize,
    setScreenSize,
    handleClick,
    isClicked,
    initialState,
    setIsClicked,
    setActiveMenu,
    setCurrentMode,
    setMode,
    setColor,
    themeSettings,
    setThemeSettings,
  }), [user, school, schoolId, setSchoolId, refreshSchool, fetchUserSchools, selectDefaultSchool, forceRefreshSchoolSelection, isInitialized, currentColor, currentMode, activeMenu, screenSize, isClicked, themeSettings, setMode, setColor]);

  // Show loading state while context is initializing
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <StateContext.Provider value={contextValue}>
      {children}
    </StateContext.Provider>
  );
};

export const useStateContext = () => useContext(StateContext);
